import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { OFFER_PATHS, SERVICE_COMPARISON_ROWS } from "@/lib/site";
import { EASE_OUT_QUART } from "@/lib/motion";

/**
 * ServiceComparison
 * Two service models presented as tabs, with a side-by-side comparison
 * row-by-row table. Replaces the older Cadence dual-card block.
 */
export default function ServiceComparison() {
  const [tab, setTab] = useState("scheduled_lot");
  const active = OFFER_PATHS.find((p) => p.id === tab) || OFFER_PATHS[0];

  return (
    <section
      data-testid="service-comparison"
      id="service-models"
      className="relative bg-white text-black"
    >
      <div className="container-lw section-lw">
        <div className="mx-auto max-w-3xl text-center">
          <div className="eyebrow justify-center text-[#4F0DFF]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#4F0DFF]" />
            Two service models
          </div>
          <h2 className="mt-5 h-display text-4xl md:text-5xl lg:text-6xl">
            Pick the level of <br />
            <span className="text-gradient-lw">dealership involvement.</span>
          </h2>
          <p className="mt-5 text-[17px] leading-relaxed text-[#4A5359]">
            Both models run on the LotWise operational standard. The
            difference is how much of the dealership inventory operation
            LotWise owns end-to-end.
          </p>
        </div>

        <Tabs
          value={tab}
          onValueChange={setTab}
          className="mt-12"
        >
          <TabsList
            data-testid="service-comparison-tabs"
            className="mx-auto grid h-auto w-full max-w-xl grid-cols-2 rounded-full border border-black/10 bg-[#F4F5F7] p-1"
          >
            {OFFER_PATHS.map((p) => (
              <TabsTrigger
                key={p.id}
                value={p.id}
                data-testid={`tab-${p.id}`}
                className="rounded-full px-4 py-3 text-sm font-semibold data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:shadow"
              >
                {p.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {OFFER_PATHS.map((path) => (
            <TabsContent
              key={path.id}
              value={path.id}
              className="mt-10"
            >
              <div className="grid gap-6 md:grid-cols-[1fr_1.15fr] md:gap-10">
                {/* Left: the service card */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={path.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.5, ease: EASE_OUT_QUART }}
                    className={`relative flex flex-col overflow-hidden rounded-[6px] border p-8 ${
                      path.premium
                        ? "border-transparent bg-black text-white"
                        : "border-black/10 bg-white text-black"
                    }`}
                  >
                    {path.premium && (
                      <>
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#4F0DFF]/40 blur-3xl"
                        />
                        <span className="relative mb-4 inline-flex w-fit items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest backdrop-blur">
                          <Sparkles className="h-3 w-3" /> Most complete
                        </span>
                      </>
                    )}

                    <div className="relative">
                      <div
                        className={`text-[10px] font-semibold uppercase tracking-[0.24em] ${
                          path.premium ? "text-[#0AB5FF]" : "text-[#79858D]"
                        }`}
                      >
                        {path.kicker}
                      </div>
                      <div className="mt-3 font-display text-3xl font-bold tracking-tight md:text-4xl">
                        {path.name}
                      </div>
                      <p
                        className={`mt-4 text-[15px] leading-relaxed ${
                          path.premium ? "text-white/80" : "text-[#4A5359]"
                        }`}
                      >
                        {path.tagline}
                      </p>
                    </div>

                    <div
                      className={`relative mt-6 border-l-2 pl-4 ${
                        path.premium
                          ? "border-[#0AB5FF] text-white/85"
                          : "border-[#4F0DFF] text-[#2A2F33]"
                      }`}
                    >
                      <div
                        className={`text-[10px] font-semibold uppercase tracking-[0.24em] ${
                          path.premium ? "text-white/60" : "text-[#79858D]"
                        }`}
                      >
                        Best for
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed">
                        {path.bestFor}
                      </p>
                    </div>

                    <ul className="relative mt-8 flex-1 space-y-3">
                      {path.includes.map((line) => (
                        <li
                          key={line}
                          className="flex items-start gap-3 text-sm"
                        >
                          <span
                            className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full ${
                              path.premium
                                ? "bg-gradient-to-br from-[#0AB5FF] to-[#4F0DFF]"
                                : "bg-black"
                            }`}
                          >
                            <Check className="h-2.5 w-2.5 text-white" />
                          </span>
                          <span
                            className={
                              path.premium ? "text-white/90" : "text-[#2A2F33]"
                            }
                          >
                            {line}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="relative mt-8 flex gap-3">
                      <Link
                        to="/request-a-visit"
                        state={{ preselect: path.id }}
                        data-testid={`comparison-cta-${path.id}`}
                        className={
                          path.premium
                            ? "btn-primary-lw"
                            : "btn-ghost-lw"
                        }
                      >
                        {path.cta}
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Right: full comparison table */}
                <div className="rounded-[6px] border border-black/10 bg-[#F4F5F7]">
                  <div className="border-b border-black/10 px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-black/50">
                    Comparison across the LotWise operation
                  </div>
                  <div className="divide-y divide-black/10">
                    {SERVICE_COMPARISON_ROWS.map((row) => {
                      const isActive =
                        active.id === "scheduled_lot" ? "scheduled" : "managed";
                      const otherValue =
                        active.id === "scheduled_lot"
                          ? row.managed
                          : row.scheduled;
                      return (
                        <div
                          key={row.label}
                          data-testid={`comparison-row-${row.label
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                          className="grid grid-cols-[1fr_1.2fr] gap-6 px-6 py-4 text-sm"
                        >
                          <div className="text-black/60">{row.label}</div>
                          <div>
                            <div className="font-semibold text-black">
                              {row[isActive]}
                            </div>
                            <div className="mt-0.5 text-xs text-black/45">
                              Other model: {otherValue}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
